import {
  trackNodeOrientationDegrees,
  type CoasterProject,
  type CoasterProjectV1,
  type TrackBehavior,
} from '../schema/index.ts';

type RideProject = CoasterProject | CoasterProjectV1;
import type {
  BankKey,
  BSplineSpan,
  SemanticSection,
  TableTrackSampler,
  TrackCompiler,
  TrackDefinition,
} from '../track/index.ts';
import { CubicBSpline } from '../track/index.ts';
import type { CoasterFamilyDefinition } from '../physics/index.ts';
import { buildFamilyPhysicsDefinition } from '../ride/family-registry.ts';

export interface ProjectTrackSpan {
  readonly index: number;
  readonly uStart: number;
  readonly uEnd: number;
  readonly startDistance: number;
  readonly endDistance: number;
  readonly controlPointIndices: readonly number[];
}

export interface CompiledProjectTrack {
  readonly sampler: TableTrackSampler;
  readonly spans: readonly ProjectTrackSpan[];
  readonly changedSpanIndices: readonly number[];
}

function behaviorToSection(
  behavior: TrackBehavior,
  id: string,
  startDistance: number,
  endDistance: number,
): SemanticSection {
  if (behavior.kind === 'lift') {
    return {
      id,
      type: 'lift',
      startDistance,
      endDistance,
      parameters: {
        targetSpeed: behavior.targetSpeedMps,
        maxAcceleration: 12,
        gain: 6,
        antiRollback: true,
      },
    };
  }
  if (behavior.kind === 'launch') {
    return {
      id,
      type: 'launch',
      startDistance,
      endDistance,
      parameters: {
        targetSpeed: behavior.targetSpeedMps,
        maxAcceleration: behavior.accelerationMps2,
      },
    };
  }
  if (behavior.kind === 'brake') {
    return {
      id,
      type: 'brake',
      startDistance,
      endDistance,
      parameters: {
        targetSpeed: behavior.targetSpeedMps,
        maxDeceleration: behavior.decelerationMps2,
      },
    };
  }
  if (behavior.kind === 'station') {
    return {
      id,
      type: 'station',
      startDistance,
      endDistance,
      parameters: {
        targetSpeed: behavior.dispatchSpeedMps,
        maxAcceleration: 12,
        maxDeceleration: 4,
        holdSeconds: behavior.holdSeconds,
      },
    };
  }
  return { id, type: 'normal', startDistance, endDistance };
}

export function distanceAtParameter(sampler: TableTrackSampler, targetU: number): number {
  const { u, distance } = sampler.table;
  const target = Math.min(1, Math.max(0, targetU));
  let low = 0;
  let high = u.length - 1;
  while (high - low > 1) {
    const middle = (low + high) >> 1;
    if (u[middle]! <= target) low = middle;
    else high = middle;
  }
  const span = u[high]! - u[low]!;
  const fraction = span > 1e-9 ? (target - u[low]!) / span : 0;
  return distance[low]! * (1 - fraction) + distance[high]! * fraction;
}

function changedSpans(
  spans: readonly BSplineSpan[],
  changedControlPointIndices: readonly number[],
): number[] {
  if (!changedControlPointIndices.length) return spans.map((span) => span.index);
  const changed = new Set(changedControlPointIndices);
  return spans
    .filter((span) => span.controlPointIndices.some((index) => changed.has(index)))
    .map((span) => span.index);
}

export function compileProjectTrackResult(
  project: RideProject,
  compiler: TrackCompiler,
  changedControlPointIndices: readonly number[] = [],
): CompiledProjectTrack {
  const modular =
    'geometryKind' in project.track && project.track.geometryKind === 'modular_piecewise_bezier';
  const base: TrackDefinition = {
    geometryKind: modular ? 'piecewise_bezier' : 'cubic_bspline',
    controlPoints: project.track.nodes.map((node) => ({ ...node.position })),
    ...(modular
      ? {
          bezierSegments: project.track.sections.map((section, index) => {
            if (!('geometry' in section) || !section.geometry)
              throw new Error(`Modular section ${section.id} has no geometry.`);
            return {
              start: { ...project.track.nodes[index]!.position },
              control1: { ...section.geometry.control1 },
              control2: { ...section.geometry.control2 },
              end: {
                ...project.track.nodes[
                  project.track.closed ? (index + 1) % project.track.nodes.length : index + 1
                ]!.position,
              },
            };
          }),
        }
      : {}),
    closed: project.track.closed,
  };
  const curve = modular ? null : new CubicBSpline(base);
  const baseSampler = compiler.compile(base, undefined, changedControlPointIndices);
  const sectionCount = Math.max(1, project.track.sections.length);
  const sections = project.track.sections.map((_, geometricIndex) => {
    // A periodic cubic B-spline span runs nearest node i+1 -> i+2 even
    // though its four control points begin at i. Rotate authored semantics
    // once so a node i -> i+1 behavior is attached to the matching geometry.
    const authoredIndex =
      project.track.closed && !modular
        ? (geometricIndex + 1) % project.track.sections.length
        : geometricIndex;
    const section = project.track.sections[authoredIndex]!;
    return behaviorToSection(
      section.behavior,
      section.id,
      distanceAtParameter(baseSampler, geometricIndex / sectionCount),
      distanceAtParameter(baseSampler, (geometricIndex + 1) / sectionCount),
    );
  });
  const nodeCount = Math.max(1, project.track.nodes.length);
  const bank: BankKey[] = project.track.nodes.map((_, geometricIndex) => {
    const authoredIndex =
      project.track.closed && !modular
        ? (geometricIndex + 1) % project.track.nodes.length
        : geometricIndex;
    return {
      distance: distanceAtParameter(baseSampler, geometricIndex / nodeCount),
      value: (trackNodeOrientationDegrees(project.track.nodes[authoredIndex]!) * Math.PI) / 180,
    };
  });
  if (project.track.closed && bank.length)
    bank.push({ distance: baseSampler.length, value: bank[0]!.value });
  const sampler = compiler.compile({ ...base, sections, bank });
  const spans = modular
    ? project.track.sections.map((_, index) => ({
        index,
        uStart: index / sectionCount,
        uEnd: (index + 1) / sectionCount,
        controlPointIndices: [
          index,
          project.track.closed
            ? (index + 1) % project.track.nodes.length
            : Math.min(index + 1, project.track.nodes.length - 1),
        ],
      }))
    : curve!.spans;
  return {
    sampler,
    spans: spans.map((span) => ({
      ...span,
      startDistance: distanceAtParameter(sampler, span.uStart),
      endDistance: distanceAtParameter(sampler, span.uEnd),
    })),
    changedSpanIndices: changedSpans(spans, changedControlPointIndices),
  };
}

export function compileProjectTrack(
  project: RideProject,
  compiler: TrackCompiler,
  changedControlPointIndices: readonly number[] = [],
): TableTrackSampler {
  return compileProjectTrackResult(project, compiler, changedControlPointIndices).sampler;
}

export function coasterFamilyForProject(project: RideProject): CoasterFamilyDefinition {
  return buildFamilyPhysicsDefinition(project as CoasterProject);
}

export function initialTrainReferenceDistance(
  project: RideProject,
  sampler?: TableTrackSampler,
): number {
  const trainOffset = Math.max(
    0.1,
    (project.train.carCount - 1) * project.train.carSpacingMeters + 0.25,
  );
  if (!sampler || !project.track.closed || !project.track.sections.length) return trainOffset;
  const stationBlockStart = project.track.sections.findIndex((section, index, sections) => {
    if (section.behavior.kind !== 'station') return false;
    const previous = sections[(index - 1 + sections.length) % sections.length];
    return previous?.behavior.kind !== 'station';
  });
  if (stationBlockStart < 0) return trainOffset;
  const sectionId = project.track.sections[stationBlockStart]!.id;
  const compiledStation = sampler.table.sections.find((section) => section.id === sectionId);
  if (!compiledStation) return trainOffset;
  return (compiledStation.startDistance + trainOffset) % sampler.length;
}
